import { useRef, useState } from 'react';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import { QRCodeSVG } from 'qrcode.react';
import { toPng } from 'html-to-image';
import { MdDownload } from 'react-icons/md';
interface DownloadStaffIdCardProps {
  staffId?: string;
  staffName?: string;
  email?: string;
  phone?: string;
  designation?: string;
  companyName?: string;
}

const DownloadStaffIdCard = ({
  staffId,
  staffName,
  companyName,
  designation,
  email,
  phone,
}: DownloadStaffIdCardProps) => {
  const qrCodeRef = useRef<HTMLDivElement>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const modifyExistingPDF = async () => {
    setIsGenerating(true);

    try {
      // 1. Create a temporary visible container for QR code conversion
      const tempContainer = document.createElement('div');
      // Clone the QR code node
      if (!qrCodeRef.current) throw new Error('QR Code element not found');
      const qrClone = qrCodeRef.current.cloneNode(true) as HTMLDivElement;
      tempContainer.appendChild(qrClone);
      document.body.appendChild(tempContainer);

      // 2. Convert QR code to PNG
      const qrCodeDataUrl = await toPng(qrClone);
      // Clean up temporary element
      document.body.removeChild(tempContainer);

      // 3. Load and modify the PDF template
      const existingPdfUrl =
        'https://m360ict.s3.ap-south-1.amazonaws.com/toab-bttf/id_card/ID+Sample.pdf';
      const existingPdfBytes = await fetch(existingPdfUrl).then((res) =>
        res.arrayBuffer()
      );
      const pdfDoc = await PDFDocument.load(existingPdfBytes);
      const [firstPage] = pdfDoc.getPages();
      const { height } = firstPage.getSize();

      // Add text content to the PDF
      const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
      const normalFont = await pdfDoc.embedFont(StandardFonts.Helvetica);

      const x = 310;
      const size = 10;
      firstPage.drawText('ID No: ', {
        x,
        y: height - 255,
        size,
        font: boldFont,
        color: rgb(0, 0, 0),
      });
      firstPage.drawText(String(staffId), {
        x: x + 110, // Adjust based on text length of the key
        y: height - 255,
        size,
        font: normalFont,
        color: rgb(0, 0, 0),
      });
      firstPage.drawText('Company Name: ', {
        x,
        y: height - 270,
        size,
        font: boldFont,
        color: rgb(0, 0, 0),
      });
      firstPage.drawText(String(companyName), {
        x: x + 110, // Adjust based on text length of the key
        y: height - 270,
        size,
        font: normalFont,
        color: rgb(0, 0, 0),
      });

      firstPage.drawText('Staff Name: ', {
        x,
        y: height - 285,
        size,
        font: boldFont,
        color: rgb(0, 0, 0),
      });
      firstPage.drawText(String(staffName), {
        x: x + 110,
        y: height - 285,
        size,
        font: normalFont,
        color: rgb(0, 0, 0),
      });

      firstPage.drawText('Designation: ', {
        x,
        y: height - 300,
        size,
        font: boldFont,
        color: rgb(0, 0, 0),
      });
      firstPage.drawText(String(designation), {
        x: x + 110,
        y: height - 300,
        size,
        font: normalFont,
        color: rgb(0, 0, 0),
      });

      // 5. Add QR code image
      const qrCodeImage = await pdfDoc.embedPng(qrCodeDataUrl);
      firstPage.drawImage(qrCodeImage, {
        x: 380,
        y: height - 410,
      });

      // 6. Generate and download the PDF
      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${staffName?.replace(' ', '_')}_ID_Card.pdf`;
      link.click();
    } catch (error) {
      console.error('Error generating ID card:', error);
      alert('Failed to generate ID card. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  // Create a single string with each key-value pair on a new line
  const qrCodeValue = `Company Name: ${companyName}\nName: ${staffName}\nDesignation:${designation}\nEmail: ${email}\nID No: ${staffId}\nPhone: ${phone}`;

  return (
    <div className='id-card-generator'>
      <div hidden>
        <div ref={qrCodeRef}>
          <QRCodeSVG value={qrCodeValue} size={100} level='H' />
        </div>
      </div>

      <span onClick={modifyExistingPDF} className='pointer'>
        {isGenerating ? 'Downloading...' : <MdDownload size={30} />}
      </span>
    </div>
  );
};

export default DownloadStaffIdCard;
